import axios from '@/lib/axios'
import Axios from 'axios'
import { ref, shallowRef } from 'vue'

import type { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

export type HotelReportType = 'local' | 'legacy' | 'operational'
export interface HotelReportAccessStatus {
	allowed: boolean
	reason: string | null
	message: string | null
}
export type HotelReportAccessMap = Record<
	HotelReportType,
	HotelReportAccessStatus
>
const types: HotelReportType[] = ['local', 'legacy', 'operational']

function createEntry(company: number) {
	return {
		company,
		consumers: 0,
		statuses: shallowRef<HotelReportAccessMap | null>(null),
		checking: ref(false),
		error: ref(''),
		generation: 0,
		controller: undefined as AbortController | undefined,
		pending: undefined as Promise<void> | undefined,
	}
}
type Entry = ReturnType<typeof createEntry>

type FetchAccess = (
	company: number,
	signal: AbortSignal
) => Promise<HotelReportAccessMap>
let requestSequence = 0
const fetchAccess: FetchAccess = async (company, signal) => {
	const { data } = await axios.get<HotelReportAccessMap>(
		`/api/companies/${company}/hotels/reports/access`,
		{
			params: { requestId: `${Date.now()}-${++requestSequence}` },
			signal,
			errorHandling: false,
			timeout: 15000,
		} as CustomAxiosRequestConfig
	)
	return data
}

/** In-flight deduplication, not a TTL cache. Nothing survives its last consumer. */
export function createHotelReportAccess(fetch: FetchAccess = fetchAccess) {
	const entries = new Map<string, Entry>()
	async function load(entry: Entry) {
		const controller = new AbortController()
		const generation = ++entry.generation
		entry.controller = controller
		entry.checking.value = true
		entry.error.value = ''
		try {
			const data = await fetch(entry.company, controller.signal)
			if (generation !== entry.generation) return
			if (!types.every((type) => typeof data?.[type]?.allowed === 'boolean'))
				throw new Error('Invalid access response')
			entry.statuses.value = data
		} catch (cause) {
			if (generation !== entry.generation || Axios.isCancel(cause)) return
			entry.statuses.value = null
			entry.error.value =
				(
					Axios.isAxiosError(cause) &&
					typeof cause.response?.data?.message === 'string'
				) ?
					cause.response.data.message
				:	'No pudimos verificar tu acceso. Reintenta; no se consultaron reservaciones.'
		} finally {
			if (generation === entry.generation) {
				entry.checking.value = false
				entry.controller = undefined
			}
		}
	}
	function refresh(entry: Entry) {
		if (!entry.pending) {
			const pending = load(entry).finally(() => {
				if (entry.pending === pending) entry.pending = undefined
			})
			entry.pending = pending
		}
		return entry.pending
	}
	function cancel(entry: Entry) {
		entry.generation++
		entry.controller?.abort()
		entry.controller = undefined
		entry.pending = undefined
		entry.checking.value = false
	}
	const onFocus = () => {
		for (const entry of entries.values()) void refresh(entry)
	}
	return {
		acquire(actor: number, company: number) {
			const key = `${actor}:${company}`
			let entry = entries.get(key)
			if (!entry) {
				if (!entries.size) window.addEventListener('focus', onFocus)
				entry = createEntry(company)
				entries.set(key, entry)
				void refresh(entry)
			}
			const current = entry
			current.consumers++
			let released = false
			return {
				entry: current,
				refresh: () => refresh(current),
				release() {
					if (released) return
					released = true
					if (--current.consumers) return
					cancel(current)
					entries.delete(key)
					if (!entries.size) window.removeEventListener('focus', onFocus)
				},
			}
		},
		/** Capture ownership now so late responses cannot affect another session. */
		denialHandler(
			actor: number | undefined,
			company: number,
			type: HotelReportType
		) {
			const key = `${actor}:${company}`
			const entry = entries.get(key)
			return (reason: string | null, message: string) => {
				if (!entry || entries.get(key) !== entry) return
				cancel(entry)
				if (reason === 'company_scope') {
					entry.statuses.value = null
					entry.error.value = message
				} else if (entry.statuses.value) {
					entry.statuses.value = {
						...entry.statuses.value,
						[type]: { allowed: false, reason, message },
					}
				}
			}
		},
	}
}

export const hotelReportAccess = createHotelReportAccess()
